import React from "react";
import { Box, Button, LinearProgress, styled, Typography, useTheme } from "@mui/material";
import { LadderIcon } from "./icons/LadderIcon";
import {
  ActionButton,
  ContinueButton,
  FullScreen,
  IconPosition,
  LadderScreenTopBar,
  LadderStartScreenLayout,
  TopLeftButton,
  TopRightLabel,
} from "./styles";
import { LadderType } from "./LadderType";
import { RichText } from "../../components/RichText";
import type { Ladder, CompletionDisplayData } from "../types";

const Scores = styled(Box)`
  label: Scores;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 480px;
  min-width: 320px;
`;

const FacetBar = styled(Box)`
  label: FacetBar;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 0;
`;

const scoresToPercentage = (score: [number, number]): number => {
  if (score[1] === 0) return 0;
  return Math.round((score[0] / score[1]) * 100);
};

export const ProfileCompletionModal = ({
  ladder,
  isFirstCompleted,
  facets,
  facetResultsHeader,
  onExit,
  onPlayAgain,
}: {
  ladder: Ladder;
  isFirstCompleted: boolean;
  facets?: CompletionDisplayData["facets"];
  facetResultsHeader?: string;
  onExit: () => void;
  onPlayAgain: () => void;
}) => {
  const theme = useTheme();
  const { experiencePoints } = ladder;

  if (!ladder) {
    return null;
  }

  const title =
    facetResultsHeader && facetResultsHeader !== "" ? facetResultsHeader : "Your results";

  const sortedFacets = [...(facets || [])].sort((a, b) => {
    const percentageA = scoresToPercentage(a.score);
    const percentageB = scoresToPercentage(b.score);
    return percentageB - percentageA;
  });

  return (
    <FullScreen>
      <LadderScreenTopBar>
        <TopLeftButton>
          <Button variant="outlined" onClick={onExit} aria-label="Exit">
            Exit
          </Button>
        </TopLeftButton>
        <TopRightLabel>
          <Typography variant="body1">
            <LadderType ladder={ladder} />
          </Typography>
        </TopRightLabel>
      </LadderScreenTopBar>
      <LadderStartScreenLayout>
        <Typography variant="h4" sx={{ color: theme.palette.primary.main }}>
          <IconPosition>
            <LadderIcon width={24} height={24} />
          </IconPosition>
          {ladder.name}
        </Typography>

        <Typography variant="h2">{title}</Typography>

        <RichText value={ladder.endScreenDescription} />

        {sortedFacets.length > 0 && (
          <Scores>
            {sortedFacets.map(({ facet, label, score }) => (
              <FacetBar key={facet}>
                <Typography variant="subtitle2">{label || facet}</Typography>
                <LinearProgress
                  variant="determinate"
                  value={scoresToPercentage(score)}
                  sx={{
                    height: "16px",
                    borderRadius: "16px",
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  {score[0]} / {score[1]}
                </Typography>
              </FacetBar>
            ))}
          </Scores>
        )}

        {isFirstCompleted && (
          <Typography variant="h4" color="success.dark">
            You earned {experiencePoints} XP
          </Typography>
        )}

        <ContinueButton handleBackNavigation={onExit} />
        <ActionButton variant="outlined" color="primary" onClick={onPlayAgain}>
          Play Again
        </ActionButton>
      </LadderStartScreenLayout>
    </FullScreen>
  );
};
